import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Shield, Send, CheckCircle2, Clock, Inbox, Search, Loader2, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  open:    { label: 'Ouvert',  cls: 'bg-amber-100 text-amber-800',   icon: Clock },
  replied: { label: 'Répondu', cls: 'bg-green-100 text-green-800',   icon: CheckCircle2 },
  closed:  { label: 'Fermé',   cls: 'bg-gray-100 text-gray-700',     icon: CheckCircle2 },
};

const AdminSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ open: 0, replied: 0, closed: 0 });
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);
  const scrollRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (!user.is_admin) return;
    loadAll();
  }, [filter]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const qs = filter === 'all' ? '' : `&status=${filter}`;
      const [tRes, sRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/support/messages?user_id=${user.id}${qs}`),
        fetch(`${API_BASE}/api/admin/support/stats?user_id=${user.id}`),
      ]);
      if (!tRes.ok) throw new Error('forbidden');
      setTickets(await tRes.json());
      setStats(await sRes.json());
    } catch (e) { toast.error('Erreur ou accès refusé'); }
    finally { setLoading(false); }
  };

  const openTicket = (t) => { setActive(t); setReply(''); };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setReplying(true);
    try {
      const res = await fetch(`${API_BASE}/api/support/messages/${active.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, body: reply }),
      });
      const data = await res.json();
      setActive(data);
      setReply('');
      loadAll();
    } catch (e) { toast.error('Erreur'); }
    finally { setReplying(false); }
  };

  const updateStatus = async (status) => {
    try {
      await fetch(`${API_BASE}/api/admin/support/messages/${active.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, status }),
      });
      setActive({ ...active, status });
      toast.success(status === 'closed' ? 'Ticket fermé' : 'Ticket rouvert');
      loadAll();
    } catch (e) { toast.error('Erreur'); }
  };

  if (!user.is_admin) {
    return <div className="p-8 text-center text-muted-foreground">Accès admin requis.</div>;
  }

  const filtered = tickets.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.subject?.toLowerCase().includes(q) || t.user_email?.toLowerCase().includes(q) || t.user_name?.toLowerCase().includes(q);
  });

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold mb-2 tracking-tight flex items-center gap-3">
          <Shield size={32} className="text-[#205F79]" />
          Admin SAV
        </h1>
        <p className="text-muted-foreground">Tickets de support utilisateurs</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Ouverts" value={stats.open} cls="text-amber-700 bg-amber-50 border-amber-200" />
        <StatCard label="Répondus" value={stats.replied} cls="text-green-700 bg-green-50 border-green-200" />
        <StatCard label="Fermés" value={stats.closed} cls="text-gray-700 bg-gray-50 border-gray-200" />
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-1 bg-gray-100 rounded-md p-1">
              {['open', 'replied', 'closed', 'all'].map(s => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  data-testid={`admin-filter-${s}`}
                  className={`px-3 py-1.5 rounded text-sm transition-all ${filter === s ? 'bg-white shadow-sm font-semibold text-[#205F79]' : 'text-gray-600 hover:text-foreground'}`}
                >
                  {s === 'open' ? 'Ouverts' : s === 'replied' ? 'Répondus' : s === 'closed' ? 'Fermés' : 'Tous'}
                </button>
              ))}
            </div>
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Rechercher email, nom, sujet..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-search" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets list */}
      <Card>
        <CardHeader><CardTitle>Tickets ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="skeleton-shimmer h-32 rounded-lg" />
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Inbox className="mx-auto text-muted-foreground mb-3" size={48} strokeWidth={1.3} />
              <p className="text-sm text-muted-foreground">Aucun ticket dans cette catégorie.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(t => {
                const st = STATUS_LABEL[t.status] || STATUS_LABEL.open;
                const StIcon = st.icon;
                const last = t.replies?.[t.replies.length - 1];
                return (
                  <button key={t.id} onClick={() => openTicket(t)} data-testid={`admin-ticket-row-${t.id}`}
                    className="w-full text-left flex items-center gap-3 p-4 border border-border rounded-md hover:border-[#205F79]/50 hover:bg-muted/40 transition-all">
                    <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${st.cls}`}>
                      <StIcon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-medium truncate">{t.subject}</p>
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        <span className="font-medium">{t.user_name}</span> · {t.user_email}
                      </p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {last?.from === 'admin' && '[Vous] '}{last?.body}
                      </p>
                    </div>
                    <p className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                      {new Date(t.updated_at).toLocaleDateString('fr-FR')}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{active?.subject}</DialogTitle>
            <DialogDescription>
              {active?.user_name} · {active?.user_email}
            </DialogDescription>
          </DialogHeader>
          <div ref={scrollRef} className="max-h-[60vh] overflow-y-auto space-y-3 py-2">
            {active?.replies?.map((r, i) => (
              <div key={i} className={`flex ${r.from === 'admin' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg px-4 py-2.5 ${r.from === 'admin' ? 'bg-[#205F79]/10 border border-[#205F79]/20' : 'bg-amber-50 border border-amber-200'}`}>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-1 opacity-70">
                    {r.from === 'admin' ? `Vous · ${r.author_name}` : `Utilisateur · ${r.author_name}`}
                  </p>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{r.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{new Date(r.created_at).toLocaleString('fr-FR')}</p>
                </div>
              </div>
            ))}
          </div>
          {active?.status !== 'closed' ? (
            <>
              <form onSubmit={handleReply} className="flex gap-2 pt-2 border-t">
                <Input placeholder="Réponse admin..." value={reply} onChange={(e) => setReply(e.target.value)} data-testid="admin-input-reply" />
                <Button type="submit" disabled={replying || !reply.trim()} className="gap-2 bg-[#205F79] hover:bg-[#184a5e]" data-testid="admin-btn-send">
                  {replying ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                </Button>
              </form>
              <Button variant="outline" onClick={() => updateStatus('closed')} className="gap-2 mt-2 text-destructive border-destructive/30 hover:bg-destructive/5" data-testid="admin-btn-close">
                <Lock size={14} /> Fermer le ticket
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => updateStatus('open')} className="gap-2 mt-2" data-testid="admin-btn-reopen">
              <Unlock size={14} /> Rouvrir le ticket
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, cls }) => (
  <div className={`p-5 rounded-lg border ${cls}`}>
    <p className="text-3xl font-bold">{value}</p>
    <p className="text-sm font-medium mt-1">{label}</p>
  </div>
);

export default AdminSupport;
